import { memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from './state.module.css'
import Button from "../components/button/button";
import MapHotels from "../components/Google map/GoogleMap"
import { filterReducer, initialFilters, passesFilters } from "./state.helper";
import filterIcon from '../icons/filter.svg'
import { AllDataContext } from "../Context/Context";
import Loading from "../components/Loading/Loading";
import { getHotelsInBounds, getStatesWithHotels, searchHotels } from "../services/api";

// GIATA sends 0/0 for hotels it never geocoded. Plotted literally they sit in
// the Atlantic, and because the map auto-fits to its pins a single one drags
// the whole view out to sea. The backend nulls these on import; this is the
// second line of defence for anything already cached or newly imported.
const isPlottable = (latitude, longitude) => {
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (lat === 0 && lng === 0) return false;
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const distanceKm = (aLat, aLng, bLat, bLng) => {
    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(h));
};

const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
};

// Some feed records are geocoded to the wrong continent — "Corfu, New York"
// sits on Corfu, Greece. Framing the map to every result lets one of those
// stretch the view across an ocean, so points far from the bulk of the results
// are excluded from the framing. They are still plotted as pins.
const MAX_FIT_DRIFT_KM = 1000;

const withoutOutliers = (positions) => {
    if (positions.length < 3) return positions;
    const centre = {
        lat: median(positions.map((p) => p.lat)),
        lng: median(positions.map((p) => p.lng)),
    };
    const kept = positions.filter((p) => distanceKm(centre.lat, centre.lng, p.lat, p.lng) <= MAX_FIT_DRIFT_KM);
    return kept.length > 0 ? kept : positions;
};

// The heading echoes whatever was typed, so "marriott" would render lowercase.
// Only the first letter of each word is raised; the rest is left as typed so
// acronyms the user capitalised stay that way.
const titleCase = (value) => String(value || "").replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

export default memo(function StateAndCity() {
    const [showFilters, setShowFilters] = useState(false)
    const [viewMode, setViewMode] = useState('menu')
    const [hotelReview, setHotelReview] = useState(false);
    const [order, setOrder] = useState('')
    const [item, setItem] = useState(null)
    const [hotels, setHotels] = useState(null)
    const [searchLoading, setSearchLoading] = useState(false)
    const [searchRefreshing, setSearchRefreshing] = useState(false)
    const [searchError, setSearchError] = useState("")
    const [currentPage, setCurrentPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [hasLoadedMore, setHasLoadedMore] = useState(false)
    const [loadMoreLoading, setLoadMoreLoading] = useState(false)
    const [mapHotels, setMapHotels] = useState(null)
    const [mapTruncated, setMapTruncated] = useState(false)
    const [mapFitLocations, setMapFitLocations] = useState([])
    const [searchParams] = useSearchParams();
    const [filtersMain, dispatch] = useReducer(filterReducer, initialFilters);
    const navigate = useNavigate();
    const { setGlobalData } = useContext(AllDataContext);
    const hasCompletedSearch = useRef(false);
    const searchQuery = searchParams.get("query")?.trim() || "";
    const itemID = searchParams.get("id") || searchParams.get("cityid");
    // Cities are addressed by name and state rather than by id, because the
    // names come from the GIATA feed and many have no row in the cities table.
    const cityName = searchParams.get("city")?.trim() || "";
    const cityState = searchParams.get("state")?.trim().toUpperCase() || "";
    const type = searchQuery
        ? 'search'
        : (cityName || searchParams.get("cityid")) ? 'city' : 'state';

    useEffect(() => {
        let isActive = true;

        async function getNewData() {
            setSearchError("");
            setSearchLoading(true);
            setSearchRefreshing(hasCompletedSearch.current);
            setCurrentPage(1);
            setHasMore(false);
            setHasLoadedMore(false);
            setHotels([]);
            setMapFitLocations([]);
            let globalData = await getStatesWithHotels();
            if (!isActive) return;
            setGlobalData(globalData)
            if (globalData) {
                const cities = Array.isArray(globalData.cities) ? globalData.cities : [];
                const states = Array.isArray(globalData.states) ? globalData.states : [];
                // A named city is looked up in the cities table only to borrow
                // its coordinates for the map; not finding one is fine, the
                // search still runs and the map centres on the first result.
                const namedCity = cityName
                    ? {
                        ...(cities.find((e) =>
                            e.name?.toLowerCase() === cityName.toLowerCase()
                            && (!cityState || e.stateAbbreviation === cityState)) || {}),
                        id: `${cityName}-${cityState}`,
                        name: cityName,
                        stateAbbreviation: cityState || undefined,
                    }
                    : null;

                const foundItem = type === 'search'
                    ? { id: 'search', name: searchQuery }
                    : namedCity
                    || (type === 'city' ? cities.find((e) => e.id === +itemID) : states.find((e) => e.abbreviation === itemID))
                    || { error: `${type === 'city' ? 'City' : 'State'} not found` }
                setItem(foundItem);

                if (foundItem.error) {
                    setSearchLoading(false);
                    setSearchRefreshing(false);
                    return;
                }

                const query = type === 'search' ? searchQuery : "";
                const options = {
                    pageSize: 8,
                    page: 1,
                    state: type === 'state' ? foundItem.abbreviation : (type === 'city' ? foundItem.stateAbbreviation : undefined),
                    city: type === 'city' ? foundItem.name : undefined,
                    reviewedOnly: hotelReview,
                    sort: order,
                };

                const result = await searchHotels(query, options);
                if (!isActive) return;

                setSearchError(result.error || "");
                setHotels(result.hotels || []);
                // Captured once per search. Deriving this from the filtered list
                // instead would re-frame the map on every slider nudge and every
                // "Load More", yanking the view out from under the user.
                // A state browse frames itself on the state's own coordinates
                // (via center/zoom below) rather than on its hotels, so that a
                // mislabelled hotel can never decide where the map opens.
                setMapFitLocations(type === 'state' ? [] : withoutOutliers(
                    (result.hotels || [])
                        .filter((hotel) => isPlottable(hotel.latitude, hotel.longitude))
                        .map((hotel) => ({ lat: Number(hotel.latitude), lng: Number(hotel.longitude) }))));
                setCurrentPage(result.page || 1);
                setHasMore(Boolean(result.hasMore));
                setSearchLoading(false);
                setSearchRefreshing(false);
                hasCompletedSearch.current = true;
            }
        }
        getNewData()

        return () => {
            isActive = false;
        };
    }, [itemID, cityName, cityState, type, searchQuery, setGlobalData, hotelReview, order]);

    const searchRequest = useMemo(() => {
        if (!item || item.error) return "";
        return {
            query: type === 'search' ? searchQuery : "",
            state: type === 'state' ? item.abbreviation : (type === 'city' ? item.stateAbbreviation : undefined),
            city: type === 'city' ? item.name : undefined,
        };
    }, [item, type, searchQuery]);

    // The city label pin is only drawn when we actually know where the city is.
    // A GIATA city with no row in the cities table has no coordinates, and
    // plotting it would put a marker at NaN.
    const mapCityPin = useMemo(() => {
        if (type !== 'city' || !item || item.error) return null;
        const lat = Number(item.latitude ?? item.latitute);
        const lng = Number(item.longitude);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { ...item, latitude: lat, longitude: lng } : null;
    }, [type, item]);

    const boundsTimer = useRef(null);
    const mapRequestId = useRef(0);

    // The list stays scoped to the search, but the map shows every hotel in the
    // visible area so it stays useful while panning away from the searched
    // place. Debounced because Google fires idle on every pan and zoom.
    const handleBoundsChange = useCallback((bounds) => {
        clearTimeout(boundsTimer.current);
        boundsTimer.current = setTimeout(async () => {
            const requestId = ++mapRequestId.current;
            const result = await getHotelsInBounds(bounds, { reviewedOnly: hotelReview });
            // A slower earlier request must not overwrite a newer viewport.
            if (requestId !== mapRequestId.current) return;
            // On failure keep whatever the map is already showing and let it
            // fall back to the list results, rather than blanking the map.
            if (result.error) return;
            setMapHotels(result.hotels || []);
            setMapTruncated(Boolean(result.truncated));
        }, 400);
    }, [hotelReview]);

    useEffect(() => () => clearTimeout(boundsTimer.current), []);

    const handleLoadMore = async () => {
        if (!searchRequest || !hasMore || loadMoreLoading) return;

        setLoadMoreLoading(true);
        const result = await searchHotels(searchRequest.query, {
            pageSize: 8,
            page: currentPage + 1,
            state: searchRequest.state,
            city: searchRequest.city,
            reviewedOnly: hotelReview,
            sort: order,
        });

        if (result.error) {
            setSearchError(result.error);
        } else {
            setHasLoadedMore(true);
            setHotels((prev) => [...(Array.isArray(prev) ? prev : []), ...(result.hotels || [])]);
            setCurrentPage(result.page || currentPage + 1);
            setHasMore(Boolean(result.hasMore));
        }

        setLoadMoreLoading(false);
    };

    const filteredHotels = useMemo(() => {
        if (!item) return [];
        const hotelsList = Array.isArray(hotels) ? hotels : [];
        return hotelsList.filter(hotel => passesFilters(hotel, filtersMain, hotelReview, item, type));
    }, [filtersMain, hotelReview, item, hotels, type]);

    const mapCenter = useMemo(() => {
        const firstHotel = filteredHotels.find((hotel) => isPlottable(hotel.latitude, hotel.longitude));
        return {
            lat: Number(item?.latitude || item?.latitute || firstHotel?.latitude || 39.8283),
            lng: Number(item?.longitude || firstHotel?.longitude || -98.5795),
        };
    }, [filteredHotels, item]);

    // Search results are always plotted, then the viewport results are added on
    // top. Listing search results first means they survive the viewport result
    // cap, so a hotel in the list is never missing from the map.
    const mapSourceHotels = useMemo(() => {
        const searchResults = Array.isArray(filteredHotels) ? filteredHotels : [];
        if (!mapHotels) return searchResults;

        const inViewport = mapHotels.filter((hotel) => passesFilters(hotel, filtersMain, hotelReview, item, type));
        const byId = new Map();
        for (const hotel of [...searchResults, ...inViewport]) {
            if (hotel && !byId.has(hotel.id)) byId.set(hotel.id, hotel);
        }
        return [...byId.values()];
    }, [mapHotels, filteredHotels, filtersMain, hotelReview, item, type]);


    const mapLocations = useMemo(() => mapSourceHotels
        .filter((elem) => isPlottable(elem.latitude, elem.longitude))
        .map((elem) => ({
            id: elem.id || elem.placeId,
            route: `/hotelreview?hotelID=${elem.id}`,
            name: elem.name,
            location: elem.location,
            source: elem.source,
            rating: elem.average_rates?.Overall,
            position: { lat: Number(elem.latitude), lng: Number(elem.longitude) }
        })), [mapSourceHotels]);

    return item && !item.error ? (
        <div className={styles.container}>
            <div className={styles.topSection}>
                <div>
                    <h1 className={styles.stateName}> {type === 'city' ? `${item.name}, ${item.stateAbbreviation}` : type === 'search' ? titleCase(item.name) : item.name}</h1>
                    <Button children='Book a Hotel' id={styles.bookBtn} onClick={() => window.open("https://expedia.com/affiliate/be2RLuN", "_blank")} />
                </div>
                <div className={styles.hotelsFilterBlock}>
                    <div>
                        <input type="checkbox" id="checkboxReview" className={styles.checkbox} checked={hotelReview} onChange={() => setHotelReview(!hotelReview)} /><label htmlFor='checkboxReview'> Hotels with reviews</label>
                    </div>
                    <img src={filterIcon} alt="filter" className={styles.filterIcon} role="button" onClick={() => { setShowFilters(!showFilters) }} />
                </div>
            </div>
            <div className={styles.changePageContainer}>
                <div className={styles.menu} id={viewMode === 'menu' ? styles.activeTab : null} role="button" onClick={() => { setViewMode('menu') }}>
                    <p>View hotels as a list</p>
                </div>
                <div className={styles.map} id={viewMode !== 'menu' ? styles.activeTab : null} role="button" onClick={() => { setViewMode('map') }}>
                    <p>View hotels on map</p>
                </div>
            </div>
            <div className={styles.mainSection}>
                <div className={styles.main1}>
                    <div className={styles.range} id={showFilters ? styles.showFilters : ''}>
                        <p className={styles.title}>Display only hotels rated higher than:</p>
                        {Object.entries(initialFilters).map(([key, value]) => (
                            <div key={`${item.id}-${key}`} style={{ position: 'relative', textTransform: "capitalize" }}>
                                <span>{key}  </span>
                                <input
                                    type="range" min="0" max="10" step='0.5'
                                    value={filtersMain[key]} className={styles.progressSlider}
                                    style={{ "--value": `${filtersMain[key] * 10}%` }}
                                    onChange={(e) => dispatch({ type: key, payload: Number(e.target.value) })}
                                />
                                <span> {filtersMain[key]}</span>
                            </div>
                        ))}
                    </div>
                    {
                        viewMode === 'menu' && <div className={styles.mapContainer}>
                            <MapHotels center={mapCenter}
                                zoom={type === 'city' || type === 'search' ? 12 : 6}
                                city={mapCityPin}
                                locations={mapLocations}
                                onBoundsChange={handleBoundsChange}
                                fitTo={mapFitLocations}
                            />
                            {mapTruncated && <p className={styles.status}>Showing {mapLocations.length} hotels in view. Zoom in to see the rest.</p>}
                        </div>
                    }
                </div>
                <div className={styles.main2} id={!showFilters ? styles.respStyles : ''} >
                    <div className={styles.main2Top} id={showFilters ? styles.showFilters : ''}>
                        <p className={styles.title}>Sort hotels by:</p>
                        {Object.entries(initialFilters).map(([key, value]) => (
                            <div style={{ marginTop: '10px', textTransform: "capitalize" }} key={key + value}>
                                <input type="checkbox" checked={key === order} id={key} className={styles.checkbox} disabled={searchLoading} onChange={() => order === key ? setOrder('') : setOrder(key)} /> <label htmlFor={key} >Highest {key}</label>
                            </div>
                        ))}
                        <div style={{ marginTop: '10px' }}>
                            <input type="checkbox" checked={order === 'name'} id="sort-name" className={styles.checkbox} disabled={searchLoading} onChange={() => order === 'name' ? setOrder('') : setOrder('name')} /> <label htmlFor="sort-name">Name (A-Z)</label>
                        </div>
                    </div>
                    {viewMode === 'menu' && <div className={styles.hotelsContainer}>
                        {searchLoading && <div className={styles.refreshingStatus} role="status" aria-live="polite"><span className={styles.spinner} aria-hidden="true" />{searchRefreshing ? 'Refreshing hotel list...' : 'Searching hotels...'}</div>}
                        {!searchLoading && searchError && <p className={styles.status}>{searchError}</p>}
                        {!searchLoading && !searchError && filteredHotels.length === 0 && (
                            <p className={styles.status}>
                                {type === 'search'
                                    ? <>No hotels match &ldquo;{item.name}&rdquo;. Try a hotel name, a city, or a state &mdash; for example &ldquo;Fairfield Ukiah&rdquo; or &ldquo;California&rdquo;.</>
                                    : 'No hotels found here yet.'}
                            </p>
                        )}
                        {!searchLoading && filteredHotels.map(elem =>
                            <div role="button" onClick={() => navigate(`/hotelreview?hotelID=${elem.id}`)} key={elem.id} className={styles.hotels}>
                                <div style={{ 'display': 'flex', justifyContent: 'space-between', gap: "10px" }}>
                                    <span style={{ fontWeight: '600', fontSize: '20px' }}>
                                        {elem.name}
                                        <i style={{ color: "#6E6E6E", display: 'block', fontWeight: "normal", fontSize: "15px", marginTop: "15px" }}>{elem.location}</i>
                                    </span>
                                    {Object.keys(elem.average_rates || {})?.length !== 0 && <span style={{ fontSize: '25px', fontWeight: '600', color: '#662D91' }}>{elem.average_rates.Overall}</span>}
                                </div>

                                {
                                    Object.keys(elem.average_rates || {})?.length !== 0 && <div style={{ marginTop: '20px' }}>
                                        <div style={{ width: '140px', display: 'flex', justifyContent: "space-between", marginBottom: '7px' }}>
                                            <span style={{ fontSize: "18px" }}>Overall</span> <span style={{ color: 'rgb(102, 45, 145)', fontSize: "18px" }}>{elem.average_rates.Overall}</span>
                                        </div>
                                        {
                                            Object.entries(elem?.average_rates).map(([key, value], i) => (
                                                key !== 'Overall' && <div key={`${i}`} style={{ width: '140px', display: 'flex', justifyContent: "space-between", marginBottom: '7px' }}>
                                                    <span style={{ fontSize: "18px" }}>{key}</span> <span style={{ color: 'rgb(102, 45, 145)', fontSize: "18px" }}>{value}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                }
                            </div>
                        )}
                        {!searchLoading && (hasMore || hasLoadedMore) && (
                            <Button
                                children={hasMore ? (loadMoreLoading ? 'Loading...' : 'Load More') : 'No More Results'}
                                disabled={loadMoreLoading || !hasMore}
                                id={styles.loadMoreBtn}
                                onClick={handleLoadMore}
                            />
                        )}
                    </div>}
                </div>
            </div>
            {
                viewMode === 'map' && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '50px' }}>
                    <MapHotels center={mapCenter}
                        zoom={type === 'city' || type === 'search' ? 12 : 6}
                        city={mapCityPin}
                        locations={mapLocations}
                                onBoundsChange={handleBoundsChange}
                                fitTo={mapFitLocations}
                    />
                    {mapTruncated && <p className={styles.status}>Showing the {mapLocations.length} highest-rated hotels in this area. Zoom in to see the rest.</p>}
                </div>
            }
        </div>
    ) : item?.error ? <div className={styles.notFound}>
        <p style={{ fontSize: "1.5rem", color: "#662D91" }}>{item.error}</p>
        <Button style={{ width: "180px", marginTop: "20px" }} onClick={() => navigate('/')}>Go Home</Button>
    </div> : <Loading />
})
